import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, Download, Calendar, Eye, User, ChevronLeft, ChevronRight, X, FileText } from 'lucide-react';
import api from '../api/axiosInstance';
import { PermissionManager } from '../utils/PermissionManager';
import { BoardPost, BoardFile } from './types';
import './css/board.css';

const BoardDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BoardPost | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [lastLoadedId, setLastLoadedId] = useState<string | null>(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageFiles, setImageFiles] = useState<BoardFile[]>([]);

  useEffect(() => {
    // 이미 로드한 게시글이면 스킵 (조회수 중복 증가 방지)
    if (lastLoadedId === id) return;
    
    const initializeDetail = async () => {
      try {
        // 사용자 정보 가져오기
        const userNameRes = await api.get('/api/UserName');
        const userInfo = userNameRes.data;
        setCurrentUserId(userInfo.id ? String(userInfo.id) : '');

        // 게시글 가져오기
        setLoading(true);
        const postRes = await api.get<BoardPost>(`/api/board/${id}`);
        setPost(postRes.data);
        setLastLoadedId(id || null);
      } catch (error) {
        console.error('Failed to initialize:', error);
        alert('게시글을 불러오는데 실패했습니다.');
        navigate('/board');
      } finally {
        setLoading(false);
      }
    };

    initializeDetail();
  }, [id]);

  useEffect(() => {
    if (post && post.files) {
      // Quill 이미지 제외하고 attachment만 미리보기에 포함
      const images = post.files.filter(file =>
        file.fileType && file.fileType.startsWith('image/') && file.source !== 'quill'
      );
      setImageFiles(images);
    }
  }, [post]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileDownload = async (fileId: number, fileName: string) => {
    try {
      const response = await api.get(`/api/board/file/${fileId}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('File download failed:', error);
      alert('파일 다운로드에 실패했습니다.');
    }
  };

  // PDF 미리보기 함수
  const handlePdfPreview = async (fileId: number) => {
    try {
      const response = await api.get(`/api/board/file/${fileId}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('PDF preview failed:', error);
      alert('PDF 미리보기에 실패했습니다.');
    }
  };

  const canEditPost = () => {
    if (!post) return false;
    // ADMIN, CA_ADMIN은 모든 글 수정 가능, 나머지는 본인 글만
    return PermissionManager.canModifyAllBoardPosts() ||
           (post.authorId && post.authorId === currentUserId);
  };

  const canDeletePost = () => {
    if (!post) return false;
    // ADMIN, CA_ADMIN은 모든 글 삭제 가능, 나머지는 본인 글만
    return PermissionManager.canModifyAllBoardPosts() ||
           (post.authorId && post.authorId === currentUserId);
  };

  const handleEdit = () => {
    navigate(`/board/write?id=${post?.id}`);
  };

  const handleDelete = async () => {
    if (!post) return;
    
    try {
      await api.delete(`/api/board/${post.id}`);
      alert('게시글이 삭제되었습니다.');
      navigate('/board');
    } catch (error) {
      console.error('Delete failed:', error);
      alert('게시글 삭제에 실패했습니다.');
    }
    setDeleteModalOpen(false);
  };

  const openImageModal = (index: number) => {
    setCurrentImageIndex(index);
    setImageModalOpen(true);
  };

  const closeImageModal = () => {
    setImageModalOpen(false);
  };

  const goToPrevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? imageFiles.length - 1 : prev - 1
    );
  };

  const goToNextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === imageFiles.length - 1 ? 0 : prev + 1
    );
  };

  // ESC 키로 모달 닫기
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!imageModalOpen) return;
      
      if (e.key === 'Escape') {
        closeImageModal();
      } else if (e.key === 'ArrowLeft') {
        goToPrevImage();
      } else if (e.key === 'ArrowRight') {
        goToNextImage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [imageModalOpen, imageFiles.length]);

  if (loading) {
    return (
      <div className="board-container">
        <div className="loading">로딩 중...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="board-container">
        <div className="error-message">게시글을 찾을 수 없습니다.</div>
      </div>
    );
  }

  return (
    <div className="board-container">
      <header className="board-header">
        <h1 className="board-title">게시판</h1>
      </header>

      <div className="post-detail">
        <div className="post-header">
          <h1 className="post-title">{post.title}</h1>
          
          <div className="post-meta">
            <div className="meta-item">
              <User size={16} />
              <span>{post.author}</span>
            </div>
            <div className="meta-item">
              <Calendar size={16} />
              <span>{formatDate(post.createdDate)}</span>
            </div>
            {post.modifiedDate && post.modifiedDate !== post.createdDate && (
              <div className="meta-item modified">
                <span>수정: {formatDate(post.modifiedDate)}</span>
              </div>
            )}
            <div className="meta-item">
              <Eye size={16} />
              <span>{post.viewCount}</span>
            </div>
          </div>
        </div>

        <div className="post-content">
          <div
            className="content-body ql-editor"
            dangerouslySetInnerHTML={{
              __html: post.content
            }}
          />
        </div>

        {/* 첨부파일 - quill 이미지 제외 */}
        {post.files && post.files.filter(f => f.source !== 'quill').length > 0 && (
          <div className="post-files">
            <h3 className="files-title">첨부파일</h3>
            <div className="files-list">
              {post.files.filter(f => f.source !== 'quill').map((file, index) => (
                <div key={file.id} className="file-item">
                  <div className="file-info">
                    <span
                      className="file-name file-name-link"
                      onClick={() => handleFileDownload(file.id, file.originalName)}
                    >
                      {file.originalName}
                    </span>
                    <span className="file-size">({formatFileSize(file.fileSize)})</span>
                  </div>
                  <div className="file-actions">
                    {/* 다운로드 버튼 */}
                    <button
                      className="file-action-btn-text"
                      onClick={() => handleFileDownload(file.id, file.originalName)}
                      title="다운로드"
                    >
                      <Download size={14} />
                      다운로드
                    </button>
                    {/* 이미지 파일인 경우 미리보기 버튼 */}
                    {file.fileType && file.fileType.startsWith('image/') && (
                      <button
                        className="file-action-btn-text"
                        onClick={() => {
                          const imgIndex = imageFiles.findIndex(img => img.id === file.id);
                          if (imgIndex !== -1) {
                            openImageModal(imgIndex);
                          }
                        }}
                        title="미리보기"
                      >
                        <Eye size={14} />
                        미리보기
                      </button>
                    )}
                    {/* PDF 파일인 경우 미리보기 버튼 */}
                    {file.fileType === 'application/pdf' && (
                      <button
                        className="file-action-btn-text"
                        onClick={() => handlePdfPreview(file.id)}
                        title="미리보기"
                      >
                        <FileText size={14} />
                        미리보기
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="post-actions-container">
          <div className="post-actions-left">
            {canEditPost() && (
              <button className="edit-btn" onClick={handleEdit}>
                <Edit size={16} />
                수정
              </button>
            )}
            {canDeletePost() && (
              <button className="delete-btn" onClick={() => setDeleteModalOpen(true)}>
                <Trash2 size={16} />
                삭제
              </button>
            )}
          </div>
          <button className="back-to-list-btn" onClick={() => navigate('/board')}>
            <ArrowLeft size={16} />
            목록으로
          </button>
        </div>
      </div>

      {deleteModalOpen && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div className="modal-title-row">
              <h2>게시글 삭제</h2>
              <button
                className="modal-close-btn"
                onClick={() => setDeleteModalOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-desc">
              이 게시글을 정말 삭제하시겠습니까?<br />
              삭제된 게시글은 복구할 수 없습니다.
            </div>
            <div className="modal-btn-row">
              <button
                className="modal-delete-btn"
                onClick={handleDelete}
              >
                삭제
              </button>
              <button
                className="modal-cancel-btn"
                onClick={() => setDeleteModalOpen(false)}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 이미지 모달 */}
      {imageModalOpen && imageFiles.length > 0 && (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          {/* 닫기 버튼 - 오버레이 레벨에 배치 */}
          <button 
            className="modal-close-btn" 
            onClick={(e) => {
              e.stopPropagation();
              closeImageModal();
            }}
          >
            <X size={24} />
          </button>

          {/* 이전 버튼 (이미지가 2개 이상일 때만 표시) - 오버레이 레벨에 배치 */}
          {imageFiles.length > 1 && (
            <button 
              className="modal-nav-btn modal-nav-prev" 
              onClick={(e) => {
                e.stopPropagation();
                goToPrevImage();
              }}
            >
              <ChevronLeft size={32} />
            </button>
          )}

          {/* 다음 버튼 (이미지가 2개 이상일 때만 표시) - 오버레이 레벨에 배치 */}
          {imageFiles.length > 1 && (
            <button 
              className="modal-nav-btn modal-nav-next" 
              onClick={(e) => {
                e.stopPropagation();
                goToNextImage();
              }}
            >
              <ChevronRight size={32} />
            </button>
          )}

          {/* 이미지 컨테이너 */}
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            {/* 이미지 */}
            <img 
              src={imageFiles[currentImageIndex]?.downloadUrl}
              alt={imageFiles[currentImageIndex]?.originalName}
              className="modal-image"
            />
          </div>

          {/* 하단 정보 - 오버레이 레벨에 배치 */}
          <div className="modal-bottom-info">
            {/* 이미지 인덱스 표시 */}
            {imageFiles.length > 1 && (
              <div className="modal-image-counter">
                {currentImageIndex + 1} / {imageFiles.length}
              </div>
            )}

            {/* 이미지 파일명 */}
            <div className="modal-image-filename">
              {imageFiles[currentImageIndex]?.originalName}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoardDetail;